import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { PremiumLock } from "@/components/Upgrade";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Sparkles, Send, Loader2, Leaf } from "lucide-react";

const SUGGESTIONS = [
  "Why are my seedlings so leggy?",
  "How often should I water tomato sprouts?",
  "When do I transplant basil outdoors?",
  "What causes damping off and how do I prevent it?",
];

export default function Assistant() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hi, I'm Sprout 🌱 — your growing assistant. Ask me anything about starting seeds, watering, transplanting, pests or harvest." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  if (user && !user.is_premium) {
    return (
      <Layout>
        <PremiumLock
          title="AI Care Assistant is a Pro feature"
          description="Chat with an AI grow coach for instant, personalized advice on any sprout."
          features={["Unlimited AI gardening questions", "Plus AI Plant Doctor & Identify", "Unlimited sprouts & planting calendar"]}
        />
      </Layout>
    );
  }

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post("/assistant", { message: msg, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: e.response?.data?.detail || "Sorry, I couldn't respond right now." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="mb-6 flex items-center gap-3">
        <div className="h-11 w-11 rounded-2xl bg-emerald-700 flex items-center justify-center shadow-sm">
          <Sparkles className="h-6 w-6 text-white" />
        </div>
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Care Assistant</h1>
          <p className="text-muted-foreground text-sm">Powered by AI · your personal grow coach</p>
        </div>
      </div>

      <div className="bg-card rounded-2xl border border-border shadow-sm flex flex-col h-[65vh]">
        <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="chat-messages">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              {m.role === "assistant" && (
                <div className="h-8 w-8 rounded-full bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center shrink-0 mr-2 mt-0.5">
                  <Leaf className="h-4 w-4 text-emerald-600" />
                </div>
              )}
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${
                  m.role === "user"
                    ? "bg-emerald-700 text-white rounded-br-sm"
                    : "bg-secondary text-secondary-foreground rounded-bl-sm"
                }`}
                data-testid={`chat-${m.role}`}
              >
                {m.text}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="h-8 w-8 rounded-full bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center shrink-0 mr-2">
                <Leaf className="h-4 w-4 text-emerald-600" />
              </div>
              <div className="bg-secondary rounded-2xl rounded-bl-sm px-4 py-3">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        {messages.length <= 1 && (
          <div className="px-5 pb-3 flex flex-wrap gap-2">
            {SUGGESTIONS.map((s) => (
              <button key={s} onClick={() => send(s)} className="text-xs px-3 py-1.5 rounded-full border border-border bg-secondary/50 hover:bg-secondary transition" data-testid="suggestion-chip">
                {s}
              </button>
            ))}
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}
          className="p-4 border-t border-border flex gap-2"
        >
          <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask about your plants…" disabled={loading} data-testid="chat-input" />
          <Button type="submit" disabled={loading || !input.trim()} className="bg-emerald-700 hover:bg-emerald-800 rounded-xl shrink-0" data-testid="chat-send-button">
            <Send className="h-4 w-4" />
          </Button>
        </form>
      </div>
    </Layout>
  );
}
